import { useEffect, useMemo, useState } from "react";
import { Link, Navigate } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowLeft, RefreshCw, Users, Mic2, TrendingUp, Filter, Globe, Activity } from "lucide-react";
import { LineChart, Line, XAxis, YAxis, Tooltip, ResponsiveContainer, CartesianGrid, BarChart, Bar } from "recharts";

import { api } from "../lib/api";
import { useAuth } from "../contexts/AuthContext";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../components/ui/tabs";

// ---------------------------------------------------------------------------
// Small building blocks
// ---------------------------------------------------------------------------

function StatCard({ label, value, sub, testid }) {
    return (
        <div className="bg-white rounded-2xl p-5 sm:p-6 border border-black/5" data-testid={testid}>
            <div className="text-[10px] font-semibold tracking-[0.22em] uppercase text-stone-500">
                {label}
            </div>
            <div className="mt-2 font-serif text-4xl sm:text-5xl font-light text-[#1A1A1A] leading-none tabular-nums">
                {value}
            </div>
            {sub && <div className="mt-2 text-xs text-stone-500">{sub}</div>}
        </div>
    );
}

function SectionHeading({ overline, title }) {
    return (
        <div className="mb-5">
            <div className="text-[10px] font-semibold tracking-[0.28em] uppercase text-terracotta">
                {overline}
            </div>
            <h2 className="mt-1.5 font-serif text-2xl sm:text-3xl font-light text-[#1A1A1A]">
                {title}
            </h2>
        </div>
    );
}

function fmtDate(iso) {
    if (!iso) return "—";
    try {
        return new Date(iso).toLocaleString(undefined, {
            month: "short", day: "numeric", hour: "numeric", minute: "2-digit"
        });
    } catch {
        return String(iso).slice(0, 19);
    }
}

// ---------------------------------------------------------------------------
// Tabs
// ---------------------------------------------------------------------------

function TodayTab({ data }) {
    const s = data.summary;
    return (
        <>
            <SectionHeading overline="Right now" title="Today's pulse" />
            <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-8">
                <StatCard label="Signups today"  value={s.signups_today}     sub={`${s.signups_yesterday} yesterday`} testid="stat-signups-today" />
                <StatCard label="Sessions today" value={s.sessions_today}    sub={`${s.sessions_7d} this week`}       testid="stat-sessions-today" />
                <StatCard label="WAS · north star" value={s.was}             sub="Weekly active speakers"             testid="stat-was" />
                <StatCard label="MAU"            value={s.mau}                sub="30-day active"                      testid="stat-mau" />
            </div>

            <SectionHeading overline="Totals" title="The whole book so far" />
            <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-8">
                <StatCard label="Total users"   value={s.users_total}     sub={`${s.users_google} via Google`} />
                <StatCard label="Onboarded"     value={s.users_onboarded} sub={`${data.summary.activation_rate_pct}% activation`} />
                <StatCard label="Activated"     value={s.users_activated} sub="≥ 1 session recorded" />
                <StatCard label="Sessions"      value={s.sessions_total}  sub={`${s.sessions_30d} last 30d`} />
            </div>

            <SectionHeading overline="30-day trend" title="Signups & sessions per day" />
            <div className="bg-white rounded-2xl p-4 sm:p-6 border border-black/5 h-72">
                <ResponsiveContainer width="100%" height="100%">
                    <LineChart data={data.trend}>
                        <CartesianGrid stroke="#00000010" vertical={false} />
                        <XAxis
                            dataKey="day"
                            tickFormatter={(d) => d.slice(5)}
                            tick={{ fill: "#78716C", fontSize: 11 }}
                            axisLine={false}
                            tickLine={false}
                            interval={4}
                        />
                        <YAxis
                            tick={{ fill: "#78716C", fontSize: 11 }}
                            axisLine={false}
                            tickLine={false}
                            allowDecimals={false}
                            width={30}
                        />
                        <Tooltip
                            contentStyle={{
                                borderRadius: 12,
                                border: "1px solid rgba(0,0,0,0.08)",
                                fontFamily: "Outfit, sans-serif",
                                fontSize: 13,
                            }}
                        />
                        <Line type="monotone" dataKey="signups"  stroke="#E06D53" strokeWidth={2} dot={false} />
                        <Line type="monotone" dataKey="sessions" stroke="#1A1A1A" strokeWidth={2} dot={false} />
                    </LineChart>
                </ResponsiveContainer>
            </div>
        </>
    );
}

function FunnelTab({ data }) {
    const max = Math.max(1, ...data.funnel.map((f) => f.count));
    return (
        <>
            <SectionHeading overline="Acquisition funnel" title="Where people drop off" />
            <div className="bg-white rounded-2xl p-5 sm:p-7 border border-black/5 space-y-4">
                {data.funnel.map((f, i) => {
                    const pct = Math.round((f.count / max) * 100);
                    const priorCount = i > 0 ? data.funnel[i - 1].count : f.count;
                    const conversion = priorCount ? Math.round((f.count / priorCount) * 100) : 100;
                    return (
                        <div key={f.step}>
                            <div className="flex items-baseline justify-between mb-1.5">
                                <div className="font-medium text-stone-800">{f.step}</div>
                                <div className="text-sm text-stone-500 tabular-nums">
                                    {f.count.toLocaleString()}
                                    {i > 0 && <span className="ml-3 text-stone-400">· {conversion}% of prev</span>}
                                </div>
                            </div>
                            <div className="h-3 bg-stone-100 rounded-full overflow-hidden">
                                <motion.div
                                    className="h-full bg-terracotta"
                                    initial={{ width: 0 }}
                                    animate={{ width: `${pct}%` }}
                                    transition={{ duration: 0.7, ease: [0.2, 0.8, 0.2, 1] }}
                                />
                            </div>
                        </div>
                    );
                })}
            </div>
        </>
    );
}

function CohortsTab({ data }) {
    return (
        <>
            <SectionHeading overline="Retention" title="Who keeps coming back" />
            <div className="bg-white rounded-2xl border border-black/5 overflow-x-auto">
                <table className="w-full text-sm">
                    <thead>
                        <tr className="text-[10px] tracking-[0.22em] uppercase text-stone-500 border-b border-black/5">
                            <th className="text-left px-5 py-3 font-semibold">Cohort week</th>
                            <th className="text-right px-4 py-3 font-semibold">Size</th>
                            {[0, 1, 2, 3].map((i) => (
                                <th key={i} className="text-right px-4 py-3 font-semibold">W+{i}</th>
                            ))}
                        </tr>
                    </thead>
                    <tbody>
                        {data.cohorts.length === 0 && (
                            <tr>
                                <td colSpan={6} className="px-5 py-8 text-center text-stone-400">
                                    Not enough data yet — cohorts need at least one week of history.
                                </td>
                            </tr>
                        )}
                        {data.cohorts.map((c) => (
                            <tr key={c.week} className="border-b border-black/5 last:border-0">
                                <td className="px-5 py-3 font-mono text-xs text-stone-600">{c.week}</td>
                                <td className="px-4 py-3 text-right tabular-nums">{c.size}</td>
                                {c.retention.map((r) => (
                                    <td
                                        key={r.week_offset}
                                        className="px-4 py-3 text-right tabular-nums"
                                        style={{
                                            background: r.pct
                                                ? `rgba(224,109,83,${Math.min(0.4, 0.05 + r.pct / 300)})`
                                                : "transparent",
                                        }}
                                    >
                                        {r.pct ? `${r.pct}%` : <span className="text-stone-300">·</span>}
                                    </td>
                                ))}
                            </tr>
                        ))}
                    </tbody>
                </table>
            </div>
        </>
    );
}

function TopPromptsTab({ data }) {
    const max = Math.max(1, ...data.top_prompts.map((p) => p.count));
    return (
        <>
            <SectionHeading overline="What people practice" title="Top prompts by session count" />
            <div className="bg-white rounded-2xl p-5 sm:p-7 border border-black/5">
                {data.top_prompts.length === 0 ? (
                    <div className="text-stone-400 text-sm">No sessions yet.</div>
                ) : (
                    <ul className="space-y-4">
                        {data.top_prompts.map((p) => (
                            <li key={p.prompt_id}>
                                <div className="flex items-baseline justify-between mb-1.5">
                                    <div className="text-stone-800">{p.title}</div>
                                    <div className="text-sm tabular-nums text-stone-500">{p.count}</div>
                                </div>
                                <div className="h-2 bg-stone-100 rounded-full overflow-hidden">
                                    <div className="h-full bg-terracotta/70" style={{ width: `${(p.count / max) * 100}%` }} />
                                </div>
                            </li>
                        ))}
                    </ul>
                )}
            </div>
        </>
    );
}

function QualityTab({ data }) {
    const q = data.quality;
    return (
        <>
            <SectionHeading overline="Product signal" title="Are we actually helping?" />
            <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-8">
                <StatCard label="Avg overall score"  value={`${q.avg_overall_score}`}       sub="out of 100" />
                <StatCard label="Avg WPM"             value={q.avg_wpm}                     sub="words per minute" />
                <StatCard label="Avg fillers/min"     value={q.avg_fillers_per_min}         sub="lower is better" />
                <StatCard label="Sessions graded"     value={q.sessions_graded}             sub="with a scorecard" />
            </div>
            <div className="bg-white rounded-2xl p-5 sm:p-7 border border-black/5 text-sm text-stone-600 leading-relaxed">
                <div className="font-medium text-[#1A1A1A] mb-2">Reality check</div>
                The landing page currently advertises <span className="italic">+24% avg score improvement</span> and{" "}
                <span className="italic">1.2M sessions graded</span>. Actual: <b>{q.sessions_graded} sessions graded</b>, avg score{" "}
                <b>{q.avg_overall_score}/100</b>. Consider wiring <code className="bg-terracotta/10 px-1.5 py-0.5 rounded">/api/insights/global</code> to
                show real numbers.
            </div>
        </>
    );
}

function RecentTab({ data }) {
    return (
        <>
            <SectionHeading overline="Live feed" title="Recent users" />
            <div className="bg-white rounded-2xl border border-black/5 overflow-x-auto mb-8">
                <table className="w-full text-sm">
                    <thead>
                        <tr className="text-[10px] tracking-[0.22em] uppercase text-stone-500 border-b border-black/5">
                            <th className="text-left px-5 py-3 font-semibold">Email</th>
                            <th className="text-left px-4 py-3 font-semibold">Provider</th>
                            <th className="text-left px-4 py-3 font-semibold">Onboarded</th>
                            <th className="text-right px-5 py-3 font-semibold">Signed up</th>
                        </tr>
                    </thead>
                    <tbody>
                        {data.recent_users.map((u) => (
                            <tr key={u.id} className="border-b border-black/5 last:border-0">
                                <td className="px-5 py-3">
                                    <div className="font-medium text-stone-800">{u.email}</div>
                                    {u.name && <div className="text-xs text-stone-400">{u.name}</div>}
                                </td>
                                <td className="px-4 py-3">
                                    <span className={`text-xs px-2 py-1 rounded-full ${u.auth_provider === "google" ? "bg-terracotta/10 text-terracotta" : "bg-stone-100 text-stone-600"}`}>
                                        {u.auth_provider}
                                    </span>
                                </td>
                                <td className="px-4 py-3 text-stone-500">{u.onboarded ? "✓" : "·"}</td>
                                <td className="px-5 py-3 text-right text-stone-500 tabular-nums">{fmtDate(u.created_at)}</td>
                            </tr>
                        ))}
                        {data.recent_users.length === 0 && (
                            <tr><td colSpan={4} className="px-5 py-8 text-center text-stone-400">No users yet.</td></tr>
                        )}
                    </tbody>
                </table>
            </div>

            <SectionHeading overline="Live feed" title="Recent sessions" />
            <div className="bg-white rounded-2xl border border-black/5 overflow-x-auto">
                <table className="w-full text-sm">
                    <thead>
                        <tr className="text-[10px] tracking-[0.22em] uppercase text-stone-500 border-b border-black/5">
                            <th className="text-left px-5 py-3 font-semibold">User</th>
                            <th className="text-left px-4 py-3 font-semibold">Prompt</th>
                            <th className="text-right px-4 py-3 font-semibold">Score</th>
                            <th className="text-right px-4 py-3 font-semibold">WPM</th>
                            <th className="text-right px-4 py-3 font-semibold">Dur</th>
                            <th className="text-right px-5 py-3 font-semibold">When</th>
                        </tr>
                    </thead>
                    <tbody>
                        {data.recent_sessions.map((s) => (
                            <tr key={s.id} className="border-b border-black/5 last:border-0">
                                <td className="px-5 py-3 text-stone-700">{s.user_email}</td>
                                <td className="px-4 py-3">
                                    <div className="text-stone-800">{s.prompt_title}</div>
                                    <div className="text-xs text-stone-400">{s.prompt_category}</div>
                                </td>
                                <td className="px-4 py-3 text-right tabular-nums">{s.overall_score ?? "—"}</td>
                                <td className="px-4 py-3 text-right tabular-nums">{s.wpm ?? "—"}</td>
                                <td className="px-4 py-3 text-right tabular-nums text-stone-500">{s.duration_sec}s</td>
                                <td className="px-5 py-3 text-right text-stone-500 tabular-nums">{fmtDate(s.created_at)}</td>
                            </tr>
                        ))}
                        {data.recent_sessions.length === 0 && (
                            <tr><td colSpan={6} className="px-5 py-8 text-center text-stone-400">No sessions yet.</td></tr>
                        )}
                    </tbody>
                </table>
            </div>
        </>
    );
}

// ---------------------------------------------------------------------------

// --- Traffic & Realtime tabs ---------------------------------------------

function BreakdownList({ items, keyName, labelKey = keyName, valueKey = "views" }) {
    const max = Math.max(1, ...items.map((i) => i[valueKey]));
    if (!items.length) return <div className="text-sm text-stone-400 py-4">No data yet.</div>;
    return (
        <ul className="space-y-3">
            {items.map((it) => (
                <li key={String(it[labelKey])}>
                    <div className="flex items-baseline justify-between mb-1">
                        <div className="text-sm text-stone-800 truncate max-w-[70%]">{it[labelKey]}</div>
                        <div className="text-xs tabular-nums text-stone-500">{it[valueKey]}</div>
                    </div>
                    <div className="h-1.5 bg-stone-100 rounded-full overflow-hidden">
                        <div className="h-full bg-terracotta/70" style={{ width: `${(it[valueKey] / max) * 100}%` }} />
                    </div>
                </li>
            ))}
        </ul>
    );
}

function BreakdownCard({ title, items, keyName }) {
    return (
        <div className="bg-white rounded-2xl p-5 sm:p-6 border border-black/5">
            <div className="text-[10px] font-semibold tracking-[0.22em] uppercase text-stone-500 mb-4">{title}</div>
            <BreakdownList items={items} keyName={keyName} labelKey={keyName} />
        </div>
    );
}

function TrafficTab({ days, setDays }) {
    const [t, setT] = useState(null);
    const [err, setErr] = useState(null);

    useEffect(() => {
        setT(null);
        api.get("/admin/traffic", { params: { days } })
            .then(({ data }) => setT(data))
            .catch((e) => setErr(e?.response?.data?.detail || e.message));
    }, [days]);

    if (err) return <div className="text-sm text-stone-500">Couldn&apos;t load traffic: {String(err)}</div>;
    if (!t) return <div className="text-sm text-stone-400">Loading…</div>;

    const tot = t.totals;
    return (
        <>
            <div className="flex items-center justify-between mb-5">
                <SectionHeading overline="Traffic" title={`Last ${t.window_days} days`} />
                <div className="flex gap-2">
                    {[1, 7, 30, 90].map((d) => (
                        <button
                            key={d}
                            onClick={() => setDays(d)}
                            className={`text-xs px-3 py-1.5 rounded-full border transition-colors ${
                                days === d
                                    ? "bg-[#1A1A1A] text-[#F6F5F2] border-[#1A1A1A]"
                                    : "border-black/10 text-stone-600 hover:border-black/30"
                            }`}
                        >
                            {d === 1 ? "24h" : `${d}d`}
                        </button>
                    ))}
                </div>
            </div>

            <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-8">
                <StatCard label="Pageviews"      value={tot.pageviews}         sub={`${tot.events} named events`} testid="stat-pageviews" />
                <StatCard label="Unique visitors" value={tot.unique_visitors}  sub="by anon id" />
                <StatCard label="Landing views"  value={tot.landing_visitors}  sub="unique on /" />
                <StatCard label="Landing → signup" value={`${tot.landing_conversion_pct}%`} sub={`${tot.signups_from_landing} signups`} />
            </div>

            <div className="bg-white rounded-2xl p-4 sm:p-6 border border-black/5 h-64 mb-8">
                <ResponsiveContainer width="100%" height="100%">
                    <BarChart data={t.trend}>
                        <CartesianGrid stroke="#00000010" vertical={false} />
                        <XAxis
                            dataKey="day"
                            tickFormatter={(d) => d.slice(5)}
                            tick={{ fill: "#78716C", fontSize: 11 }}
                            axisLine={false}
                            tickLine={false}
                            interval={t.trend.length > 15 ? 4 : 0}
                        />
                        <YAxis tick={{ fill: "#78716C", fontSize: 11 }} axisLine={false} tickLine={false} allowDecimals={false} width={30} />
                        <Tooltip contentStyle={{ borderRadius: 12, border: "1px solid rgba(0,0,0,0.08)", fontSize: 13 }} />
                        <Bar dataKey="pageviews" fill="#E06D53" radius={[6, 6, 0, 0]} />
                        <Bar dataKey="visitors"  fill="#1A1A1A" radius={[6, 6, 0, 0]} />
                    </BarChart>
                </ResponsiveContainer>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
                <BreakdownCard title="Top pages"    items={t.top_pages}  keyName="path" />
                <BreakdownCard title="Sources"      items={t.sources}    keyName="source" />
                <BreakdownCard title="Referrers"    items={t.referrers}  keyName="host" />
                <BreakdownCard title="UTM sources"  items={t.utm_sources} keyName="utm_source" />
            </div>

            <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-4">
                <BreakdownCard title="Device"   items={t.devices}   keyName="device" />
                <BreakdownCard title="OS"       items={t.os}        keyName="os" />
                <BreakdownCard title="Browser"  items={t.browsers}  keyName="browser" />
            </div>

            <div className="bg-white rounded-2xl p-5 sm:p-6 border border-black/5">
                <div className="text-[10px] font-semibold tracking-[0.22em] uppercase text-stone-500 mb-4">
                    Named events
                </div>
                <BreakdownList items={t.top_events} keyName="name" labelKey="name" valueKey="count" />
            </div>
        </>
    );
}

function RealtimeTab() {
    const [r, setR] = useState(null);

    useEffect(() => {
        let alive = true;
        const load = () =>
            api.get("/admin/realtime")
               .then(({ data }) => { if (alive) setR(data); })
               .catch(() => {});
        load();
        const id = setInterval(load, 5000); // poll every 5s
        return () => { alive = false; clearInterval(id); };
    }, []);

    if (!r) return <div className="text-sm text-stone-400">Listening…</div>;

    return (
        <>
            <SectionHeading overline="Live" title="Right now, in the last 5 minutes" />
            <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-8">
                <StatCard label="Active · 5min"    value={r.active_5min}  sub="unique visitors" />
                <StatCard label="Active · 30min"   value={r.active_30min} sub="last half hour" />
                <StatCard label="Top page now"     value={r.top_paths_now[0]?.path || "—"} sub={`${r.top_paths_now[0]?.views ?? 0} views`} />
                <StatCard label="Refresh"          value="5s" sub="polling automatically" />
            </div>

            <div className="bg-white rounded-2xl p-4 sm:p-6 border border-black/5 h-56 mb-8">
                <ResponsiveContainer width="100%" height="100%">
                    <BarChart data={r.per_minute}>
                        <CartesianGrid stroke="#00000010" vertical={false} />
                        <XAxis dataKey="minute" tickFormatter={(m) => `${m}m`} tick={{ fill: "#78716C", fontSize: 11 }} axisLine={false} tickLine={false} interval={4} />
                        <YAxis tick={{ fill: "#78716C", fontSize: 11 }} axisLine={false} tickLine={false} allowDecimals={false} width={30} />
                        <Tooltip contentStyle={{ borderRadius: 12, border: "1px solid rgba(0,0,0,0.08)", fontSize: 13 }} />
                        <Bar dataKey="pageviews" fill="#E06D53" radius={[4, 4, 0, 0]} />
                    </BarChart>
                </ResponsiveContainer>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
                <BreakdownCard title="Active pages" items={r.top_paths_now} keyName="path" />
                <div className="bg-white rounded-2xl p-5 sm:p-6 border border-black/5">
                    <div className="text-[10px] font-semibold tracking-[0.22em] uppercase text-stone-500 mb-4">Live pageview feed</div>
                    {r.recent.length === 0 ? (
                        <div className="text-sm text-stone-400">Silence — nobody&apos;s on the site right now.</div>
                    ) : (
                        <ul className="space-y-2 text-sm max-h-72 overflow-y-auto">
                            {r.recent.map((e, i) => (
                                <li key={i} className="flex items-baseline justify-between gap-3 border-b border-black/5 pb-2 last:border-0">
                                    <div className="min-w-0">
                                        <div className="font-medium text-stone-800 truncate">{e.path}</div>
                                        <div className="text-xs text-stone-500 truncate">
                                            {e.user} · {e.source} · {e.device}
                                        </div>
                                    </div>
                                    <div className="text-xs text-stone-400 tabular-nums whitespace-nowrap">{fmtDate(e.at)}</div>
                                </li>
                            ))}
                        </ul>
                    )}
                </div>
            </div>
        </>
    );
}

// -------------------------------------------------------------------------

export default function Admin() {
    const { user, loading } = useAuth();
    const [data, setData] = useState(null);
    const [err, setErr] = useState(null);
    const [includeQa, setIncludeQa] = useState(false);
    const [refreshing, setRefreshing] = useState(false);
    const [trafficDays, setTrafficDays] = useState(7);

    const fetchMetrics = useMemo(
        () => async () => {
            setRefreshing(true);
            try {
                const { data } = await api.get("/admin/metrics", { params: { include_qa: includeQa } });
                setData(data);
                setErr(null);
            } catch (e) {
                setErr(e?.response?.data?.detail || e.message || "Failed to load");
            } finally {
                setRefreshing(false);
            }
        },
        [includeQa]
    );

    useEffect(() => {
        document.title = "Cadence — Admin";
        if (user) fetchMetrics();
    }, [user, fetchMetrics]);

    if (loading) {
        return <div className="min-h-screen flex items-center justify-center font-serif italic text-stone-400 text-2xl">Cadence</div>;
    }
    if (!user) return <Navigate to="/auth" replace />;
    // 404 for non-admins — mirrors backend gating.
    if (err && String(err).toLowerCase().includes("not found")) {
        return (
            <main className="min-h-screen flex items-center justify-center bg-[#F6F5F2] p-6" data-testid="admin-403">
                <div className="text-center">
                    <div className="font-serif text-5xl font-light text-stone-800 mb-2">404</div>
                    <div className="text-stone-500">This page doesn&apos;t exist.</div>
                    <Link to="/home" className="mt-6 inline-block text-terracotta text-sm underline">Back to Cadence</Link>
                </div>
            </main>
        );
    }

    return (
        <main className="min-h-screen bg-[#F6F5F2]" data-testid="admin-page">
            <header className="sticky top-0 z-40 border-b border-black/5 bg-[#F6F5F2]/85 backdrop-blur-xl">
                <div className="max-w-7xl mx-auto px-5 sm:px-8 h-16 flex items-center justify-between">
                    <div className="flex items-center gap-4">
                        <Link to="/home" className="inline-flex items-center gap-1.5 text-sm text-stone-600 hover:text-stone-900">
                            <ArrowLeft size={16} strokeWidth={1.5} /> App
                        </Link>
                        <span className="text-[10px] tracking-[0.28em] uppercase font-semibold text-terracotta">Admin</span>
                    </div>
                    <div className="flex items-center gap-3">
                        <label className="inline-flex items-center gap-2 text-xs text-stone-600 cursor-pointer select-none" data-testid="admin-toggle-qa">
                            <input
                                type="checkbox"
                                className="accent-terracotta"
                                checked={includeQa}
                                onChange={(e) => setIncludeQa(e.target.checked)}
                            />
                            <Filter size={13} strokeWidth={1.5} /> Include QA test users
                        </label>
                        <button
                            onClick={fetchMetrics}
                            disabled={refreshing}
                            className="inline-flex items-center gap-1.5 text-sm text-stone-600 hover:text-stone-900 disabled:opacity-50"
                            data-testid="admin-refresh"
                        >
                            <RefreshCw size={14} strokeWidth={1.5} className={refreshing ? "animate-spin" : ""} />
                            Refresh
                        </button>
                    </div>
                </div>
            </header>

            <div className="max-w-7xl mx-auto px-5 sm:px-8 pt-8 pb-24">
                <div className="mb-10">
                    <h1 className="font-serif text-4xl sm:text-5xl font-light text-[#1A1A1A]">
                        The <span className="italic text-terracotta">real</span> Cadence.
                    </h1>
                    <p className="mt-3 text-stone-600 max-w-xl">
                        Live numbers pulled from your MongoDB. What&apos;s actually happening — not what the landing page says.
                    </p>
                </div>

                {err && !String(err).toLowerCase().includes("not found") && (
                    <div className="bg-white border border-terracotta/30 rounded-2xl p-5 mb-6 text-sm text-stone-700">
                        Couldn&apos;t load metrics: <span className="font-mono">{String(err)}</span>
                    </div>
                )}

                {!data ? (
                    <div className="text-stone-400 text-sm">Loading…</div>
                ) : (
                    <Tabs defaultValue="today" className="w-full">
                        <TabsList className="mb-6 bg-white border border-black/5 flex-wrap h-auto">
                            <TabsTrigger value="today"    data-testid="admin-tab-today">    <Users size={14} className="mr-1.5" /> Today </TabsTrigger>
                            <TabsTrigger value="traffic"  data-testid="admin-tab-traffic">  <Globe size={14} className="mr-1.5" /> Traffic </TabsTrigger>
                            <TabsTrigger value="realtime" data-testid="admin-tab-realtime"> <Activity size={14} className="mr-1.5" /> Realtime </TabsTrigger>
                            <TabsTrigger value="funnel"   data-testid="admin-tab-funnel">   Funnel </TabsTrigger>
                            <TabsTrigger value="cohorts"  data-testid="admin-tab-cohorts">  Cohorts </TabsTrigger>
                            <TabsTrigger value="prompts"  data-testid="admin-tab-prompts">  <Mic2 size={14} className="mr-1.5" /> Top prompts </TabsTrigger>
                            <TabsTrigger value="quality"  data-testid="admin-tab-quality">  <TrendingUp size={14} className="mr-1.5" /> Quality </TabsTrigger>
                            <TabsTrigger value="recent"   data-testid="admin-tab-recent">   Live feed </TabsTrigger>
                        </TabsList>
                        <TabsContent value="today">    <TodayTab      data={data} /> </TabsContent>
                        <TabsContent value="traffic">  <TrafficTab    days={trafficDays} setDays={setTrafficDays} /> </TabsContent>
                        <TabsContent value="realtime"> <RealtimeTab /> </TabsContent>
                        <TabsContent value="funnel">   <FunnelTab     data={data} /> </TabsContent>
                        <TabsContent value="cohorts">  <CohortsTab    data={data} /> </TabsContent>
                        <TabsContent value="prompts">  <TopPromptsTab data={data} /> </TabsContent>
                        <TabsContent value="quality">  <QualityTab    data={data} /> </TabsContent>
                        <TabsContent value="recent">   <RecentTab     data={data} /> </TabsContent>
                    </Tabs>
                )}

                <div className="mt-14 text-[10px] tracking-[0.22em] uppercase text-stone-400">
                    Generated {data ? fmtDate(data.generated_at) : "—"} · {data?.exclude_qa ? "QA users excluded" : "QA included"}
                </div>
            </div>
        </main>
    );
}
